import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/contexts/AuthContext";
import { Send, Upload, Loader2, X, Check, Clock, Users, RefreshCw, AlertTriangle } from "lucide-react";

export default function AdminBroadcasts() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", template_name: "", template_language: "ar",
    params_template: [], scheduled_at: "", csv_text: "", plain_numbers: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        api.get("/admin/whatsapp/templates").catch((e) => ({ data: { items: [], _error: e?.response?.data?.detail || e.message } })),
        api.get("/admin/whatsapp/broadcasts"),
      ]);
      setTemplates(tRes.data.items || []);
      if (tRes.data._error) setError(`Templates: ${tRes.data._error}`);
      setBroadcasts(bRes.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const selectedTemplate = templates.find((t) => t.name === form.template_name);
  const placeholderCount = selectedTemplate?.placeholder_count || 0;

  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.csv_text && !body.plain_numbers) {
        setError(lang === "ar" ? "أدخل CSV أو قائمة أرقام" : "Enter CSV or phone list");
        setSubmitting(false); return;
      }
      if (!body.template_name) {
        setError(lang === "ar" ? "اختر قالباً معتمداً" : "Choose an approved template");
        setSubmitting(false); return;
      }
      if (body.scheduled_at === "") body.scheduled_at = null;
      body.params_template = body.params_template.slice(0, placeholderCount);
      await api.post("/admin/whatsapp/broadcasts", body);
      setToast(lang === "ar" ? "🎉 تم إنشاء الحملة" : "🎉 Broadcast created");
      setShowForm(false);
      setForm({ name: "", template_name: "", template_language: "ar", params_template: [], scheduled_at: "", csv_text: "", plain_numbers: "" });
      await loadAll();
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
    setSubmitting(false);
  };

  const cancelBroadcast = async (bid) => {
    if (!window.confirm(lang === "ar" ? "إلغاء الحملة؟" : "Cancel broadcast?")) return;
    try {
      await api.post(`/admin/whatsapp/broadcasts/${bid}/cancel`);
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const statusColor = { pending: "bg-amber-100 text-amber-800", scheduled: "bg-sky-100 text-sky-800",
    running: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800", cancelled: "bg-stone-100 text-stone-700" };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-broadcasts-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Send className="text-emerald-700" size={28} />
            {lang === "ar" ? "حملات WhatsApp" : "WhatsApp Broadcasts"}
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            {lang === "ar" ? "أرسل رسائل جماعية عبر قوالب Meta المعتمدة" : "Send bulk messages via Meta-approved templates"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="refresh-broadcasts-btn" variant="outline" onClick={loadAll} disabled={loading} className="rounded-xl">
            <RefreshCw size={14} className={`me-2 ${loading ? "animate-spin" : ""}`} />
            {lang === "ar" ? "تحديث" : "Refresh"}
          </Button>
          <Button data-testid="new-broadcast-btn" onClick={() => setShowForm(!showForm)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            {showForm ? <X size={14} className="me-2" /> : <Send size={14} className="me-2" />}
            {showForm ? (lang === "ar" ? "إغلاق" : "Close") : (lang === "ar" ? "حملة جديدة" : "New broadcast")}
          </Button>
        </div>
      </div>

      {toast && <div data-testid="broadcast-toast" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800">{toast}</div>}
      {error && <div data-testid="broadcast-error" className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800 flex items-start gap-2"><AlertTriangle size={16} className="text-red-600 mt-0.5" />{String(error)}</div>}

      {/* New broadcast form */}
      {showForm && (
        <Card className="rounded-3xl border-emerald-200" data-testid="broadcast-form">
          <CardContent className="p-7 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-stone-800 block mb-2">{lang === "ar" ? "اسم الحملة (اختياري)" : "Broadcast name (optional)"}</label>
                <Input data-testid="broadcast-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === "ar" ? "عرض رمضان" : "Ramadan offer"} />
              </div>
              <div>
                <label className="text-sm font-semibold text-stone-800 block mb-2">{lang === "ar" ? "قالب معتمد" : "Approved template"}</label>
                <Select value={form.template_name} onValueChange={(v) => setForm({ ...form, template_name: v, template_language: templates.find((t) => t.name === v)?.language || "ar", params_template: [] })}>
                  <SelectTrigger data-testid="broadcast-template-select"><SelectValue placeholder={lang === "ar" ? "اختر قالب…" : "Pick template…"} /></SelectTrigger>
                  <SelectContent>
                    {templates.filter((t) => t.status === "APPROVED").map((t) => (
                      <SelectItem key={`${t.name}-${t.language}`} value={t.name}>{t.name} ({t.language})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templates.length === 0 && <p className="text-xs text-amber-700 mt-1">{lang === "ar" ? "⚠ لا توجد قوالب معتمدة" : "⚠ No approved templates"}</p>}
              </div>
            </div>

            {selectedTemplate?.body && (
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-xs text-stone-700 whitespace-pre-wrap font-mono" dir="auto">
                {selectedTemplate.body}
              </div>
            )}

            {placeholderCount > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-stone-800 block">{lang === "ar" ? `متغيرات القالب (${placeholderCount})` : `Template variables (${placeholderCount})`}</label>
                {Array.from({ length: placeholderCount }).map((_, i) => (
                  <Input key={i} data-testid={`broadcast-param-${i}`} value={form.params_template[i] || ""}
                    onChange={(e) => { const p = [...form.params_template]; p[i] = e.target.value; setForm({ ...form, params_template: p }); }}
                    placeholder={`{{${i + 1}}} — ${lang === "ar" ? "قيمة أو ${name}" : "value or ${name}"}`} />
                ))}
                <p className="text-xs text-stone-500">{lang === "ar" ? "استخدم ${name} للاستبدال حسب المستلم" : "Use ${name} for per-recipient substitution"}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-semibold text-stone-800 block mb-2">{lang === "ar" ? "المستلمون (أحد الطريقتين)" : "Recipients (one of two)"}</label>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-stone-600 mb-1">{lang === "ar" ? "أرقام سطر لكل مستلم" : "Phone numbers, one per line"}</p>
                  <textarea data-testid="broadcast-plain-numbers" rows={4} value={form.plain_numbers}
                    onChange={(e) => setForm({ ...form, plain_numbers: e.target.value, csv_text: "" })}
                    placeholder="96895xxxxxxx\n96895yyyyyyy" dir="ltr"
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div className="text-center text-xs text-stone-500 py-1">— {lang === "ar" ? "أو" : "or"} —</div>
                <div>
                  <p className="text-xs text-stone-600 mb-1">{lang === "ar" ? "CSV: phone,name,var1,var2..." : "CSV: phone,name,var1,var2..."}</p>
                  <textarea data-testid="broadcast-csv-input" rows={4} value={form.csv_text}
                    onChange={(e) => setForm({ ...form, csv_text: e.target.value, plain_numbers: "" })}
                    placeholder="phone,name&#10;96895xxxxxxx,Ahmed&#10;96895yyyyyyy,Sara" dir="ltr"
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-stone-800 block mb-2">{lang === "ar" ? "جدولة (اختياري)" : "Schedule (optional)"}</label>
              <Input data-testid="broadcast-schedule" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
              <p className="text-xs text-stone-500 mt-1">{lang === "ar" ? "اتركه فارغاً للإرسال الفوري" : "Leave empty for immediate send"}</p>
            </div>

            <Button data-testid="submit-broadcast-btn" onClick={submit} disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-12 px-6">
              {submitting ? <Loader2 className="animate-spin me-2" size={16} /> : <Check className="me-2" size={16} />}
              {lang === "ar" ? "إنشاء الحملة" : "Create broadcast"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Broadcast list */}
      <Card className="rounded-3xl border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-stone-500"><Loader2 className="animate-spin mx-auto mb-2" /> {lang === "ar" ? "تحميل..." : "Loading..."}</div>
          ) : broadcasts.length === 0 ? (
            <div className="p-12 text-center text-stone-500">
              <Upload className="mx-auto mb-3 text-stone-400" size={40} />
              <p className="text-sm">{lang === "ar" ? "لا توجد حملات بعد" : "No broadcasts yet"}</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {broadcasts.map((b) => (
                <div key={b.id} data-testid={`broadcast-row-${b.id}`} className="p-5 flex items-center gap-4 flex-wrap hover:bg-stone-50">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-stone-900">{b.name || b.template_name}</p>
                      <Badge className={`text-[10px] ${statusColor[b.status] || "bg-stone-100 text-stone-700"} hover:bg-current`}>{b.status}</Badge>
                    </div>
                    <p className="text-xs text-stone-500 flex items-center gap-2">
                      <span className="flex items-center gap-1"><Users size={12} /> {b.total || 0}</span>
                      <span>✓ {b.sent || 0}</span>
                      <span className="text-red-600">✗ {b.failed || 0}</span>
                      {b.scheduled_at && <span className="flex items-center gap-1"><Clock size={12} /> {new Date(b.scheduled_at).toLocaleString()}</span>}
                    </p>
                  </div>
                  {(b.status === "pending" || b.status === "scheduled" || b.status === "running") && (
                    <Button data-testid={`cancel-broadcast-${b.id}`} variant="outline" size="sm" onClick={() => cancelBroadcast(b.id)} className="text-red-700 border-red-200 hover:bg-red-50">
                      {lang === "ar" ? "إلغاء" : "Cancel"}
                    </Button>
                  )}
                  <Button data-testid={`view-broadcast-${b.id}`} variant="ghost" size="sm" onClick={() => navigate(`/admin/broadcasts/${b.id}`)}>
                    {lang === "ar" ? "التفاصيل ←" : "Details →"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
